import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ImagePlus, Trash2, Globe, MapPin } from "lucide-react";
import panelApi from "@/lib/panelApi";
import { apiError } from "@/lib/api";
import { useLogos } from "@/lib/useLogo";
import { useBase } from "@/context/BaseContext";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/DataState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MAX = 1024 * 1024;

const readLogo = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = reject;
  r.onload = () => {
    const img = new Image();
    img.onload = () => resolve({ data: r.result, w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = r.result;
  };
  r.readAsDataURL(file);
});

function LogoSlot({ scope, logo, hint }) {
  const qc = useQueryClient();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); await qc.invalidateQueries({ queryKey: ["panel-logos"] }); toast.success(ok); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg"].includes(f.type)) { toast.error("Envie um arquivo PNG ou JPG"); return; }
    if (f.size > MAX) { toast.error("A imagem deve ter no máximo 1 MB"); return; }
    run(async () => panelApi.put(`/panel/logo/${scope}`, await readLogo(f)), "Logo salva");
  };
  return (
    <div className="space-y-3">
      <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-700 bg-white/95" data-testid={`logo-preview-${scope}`}>
        {logo?.data ? <img src={logo.data} alt="Logo" className="max-h-24 max-w-[80%] object-contain" data-testid={`logo-img-${scope}`} />
          : <p className="text-sm text-slate-500" data-testid={`logo-empty-${scope}`}>{hint}</p>}
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} data-testid={`logo-input-${scope}`} />
      <div className="flex gap-2">
        <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy} onClick={() => input.current?.click()} data-testid={`logo-upload-${scope}`}>
          <ImagePlus className="h-4 w-4" /> {logo?.data ? "Trocar logo" : "Enviar logo"}
        </Button>
        {logo?.data && (
          <Button variant="outline" className="border-slate-700 text-slate-300 hover:text-rose-300" disabled={busy}
            onClick={() => run(() => panelApi.delete(`/panel/logo/${scope}`), "Logo removida")} data-testid={`logo-remove-${scope}`}>
            <Trash2 className="h-4 w-4" /> Remover
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500">PNG ou JPG, até 1 MB.</p>
    </div>
  );
}

function Card({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
      <p className="flex items-center gap-2 text-sm font-bold text-slate-100"><Icon className="h-4 w-4 text-orange-400" />{title}</p>
      <p className="mb-4 mt-1 text-xs text-slate-500">{desc}</p>
      {children}
    </div>
  );
}

export default function Configuracoes() {
  const logos = useLogos();
  const { bases, selectedBaseId } = useBase();
  const [baseId, setBaseId] = useState(selectedBaseId || "");
  const bid = baseId || bases[0]?.id || "";
  if (logos.isLoading) return <Loading />;
  const d = logos.data || {};
  return (
    <div className="space-y-4" data-testid="config-page">
      <div>
        <h2 className="text-base font-semibold text-slate-100 md:text-lg">Logo da empresa</h2>
        <p className="text-sm text-slate-500">Aparece no cabeçalho dos relatórios e PDFs. A logo da base tem prioridade sobre a global.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Card icon={Globe} title="Logo global" desc="Vale para todas as bases que não têm logo própria.">
          <LogoSlot scope="global" logo={d.global} hint="Nenhuma logo global" />
        </Card>
        <Card icon={MapPin} title="Logo por base" desc="Substitui a logo global nos relatórios da base escolhida.">
          {!bases.length ? <p className="text-sm text-slate-500">Nenhuma base cadastrada.</p> : (
            <div className="space-y-3">
              <Select value={bid} onValueChange={setBaseId}>
                <SelectTrigger className="border-slate-700 bg-slate-950 text-slate-100" data-testid="logo-base-select"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  {bases.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`logo-base-opt-${b.id}`}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <LogoSlot key={bid} scope={bid} logo={d.bases?.[bid]} hint={d.global?.data ? "Sem logo própria · usa a global" : "Sem logo própria"} />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
